import type {
  WorkCategory,
  CatalogEntry,
  CollectionInfo,
  SeriesInfo,
  RecommendItem,
  FaqItem,
  HeroStat,
  SearchDirection,
} from "@/types/catalog";

export const heroStats: HeroStat[] = [
  {
    id: "s1",
    value: "10+",
    label: "作品条目方向",
    desc: "覆盖剧情、企划、合集、特典等常见分类",
  },
  {
    id: "s2",
    value: "6",
    label: "作品类型",
    desc: "按内容形态划分，便于快速定位想看的类型",
  },
  {
    id: "s3",
    value: "4",
    label: "合集组织方式",
    desc: "年份、主题、精选与纪念四种整理思路",
  },
  {
    id: "s4",
    value: "9",
    label: "标签筛选",
    desc: "在本页即可筛选与检索已收录条目",
  },
];

export const searchDirections: SearchDirection[] = [
  {
    id: "d1",
    keyword: "作品",
    desc: "偏整体的作品范围，先了解有哪些内容形态，再决定深入哪一类。",
  },
  {
    id: "d2",
    keyword: "合集",
    desc: "把多部内容重新编排成一套，适合想成体系浏览、避免零散挑选的人。",
  },
  {
    id: "d3",
    keyword: "列表 / 大全",
    desc: "期望看到一份可检索、可筛选的条目清单，用编号与标签快速定位。",
  },
  {
    id: "d4",
    keyword: "系列",
    desc: "连续推出的主题单元，条目之间有承接关系，顺序浏览更连贯。",
  },
  {
    id: "d5",
    keyword: "推荐",
    desc: "希望有人给出观看顺序与理由，减少挑选成本，直接看口碑方向。",
  },
];

export const workCategories: WorkCategory[] = [
  {
    id: "c1",
    name: "剧情长片",
    tagline: "完整叙事 · 单部发行",
    desc: "以完整故事线为核心的常规长篇，单部即可独立观看，是作品体系中最基础的一类。",
    count: "常作为主体内容持续更新",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20cinematic%20still%20life%20of%20an%20old%20film%20reel%20and%20script%20paper%20on%20a%20dark%20wooden%20table%2C%20warm%20amber%20rim%20light%2C%20deep%20brown%20shadows%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20moody%20warm%20tones%2C%20no%20people&width=800&height=600&seq=yua-cat-01&orientation=landscape",
  },
  {
    id: "c2",
    name: "企划专题",
    tagline: "主题设定 · 连载发行",
    desc: "围绕特定设定或场景打造的策划型内容，每期主题不同，适合按兴趣挑着看。",
    count: "按企划周期不定期推出",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20theatre%20stage%20curtain%20with%20warm%20golden%20spotlight%20and%20dust%20particles%20in%20the%20air%2C%20deep%20brown%20and%20terracotta%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20moody%20cinematic%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-cat-02&orientation=landscape",
  },
  {
    id: "c3",
    name: "纪念合集",
    tagline: "节点回顾 · 套装发行",
    desc: "在出道、周年等重要节点推出的回顾型套装，通常把过往代表作集中收录。",
    count: "围绕重要时间节点推出",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20collection%20of%20stacked%20vintage%20film%20canisters%20with%20warm%20amber%20backlight%20on%20a%20dark%20surface%2C%20deep%20brown%20tones%2C%20subtle%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20nostalgic%20moody%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-cat-03&orientation=landscape",
  },
  {
    id: "c4",
    name: "精选合集",
    tagline: "优中选优 · 合辑发行",
    desc: "从大量内容中挑选高口碑条目重新编排，适合时间有限、想快速看到精华的人。",
    count: "按口碑与主题不定期更新",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20golden%20award%20ribbon%20and%20scattered%20film%20strips%20on%20dark%20velvet%20fabric%2C%20warm%20amber%20light%2C%20deep%20brown%20shadows%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20luxury%20moody%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-cat-04&orientation=landscape",
  },
  {
    id: "c5",
    name: "跨界联动",
    tagline: "外部合作 · 专题发行",
    desc: "与品牌、节目或其他领域合作的联动内容，风格往往区别于常规作品。",
    count: "按合作计划不定期推出",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20neon%20lit%20city%20street%20at%20night%20seen%20through%20rain%20streaked%20glass%2C%20warm%20amber%20and%20terracotta%20reflections%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20moody%20cinematic%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-cat-05&orientation=landscape",
  },
  {
    id: "c6",
    name: "数字限定",
    tagline: "线上发行 · 短篇为主",
    desc: "只在线上平台发行、篇幅相对精简的内容，更新灵活，适合碎片时间浏览。",
    count: "线上优先、更新较灵活",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20glowing%20smartphone%20and%20floating%20light%20particles%20in%20a%20dark%20studio%2C%20warm%20amber%20glow%2C%20deep%20brown%20background%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20modern%20moody%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-cat-06&orientation=landscape",
  },
];

export const catalogTags: string[] = [
  "全部",
  "剧情",
  "企划",
  "合集",
  "纪念",
  "特典",
  "精选",
  "数字",
  "海外",
];

export const catalogEntries: CatalogEntry[] = [
  {
    id: "e1",
    code: "A-01",
    title: "出道纪念企划",
    type: "纪念",
    period: "出道初期",
    duration: "常规长篇",
    format: "单部发行",
    tags: ["纪念", "合集"],
    desc: "在出道节点推出的纪念型内容，通常用于回顾起步阶段的作品方向，适合作为了解整体脉络的起点。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20cinematic%20poster%20of%20a%20single%20spotlight%20on%20dark%20stage%20floor%20with%20red%20velvet%20curtain%20edge%2C%20warm%20amber%20tones%2C%20deep%20brown%20shadows%2C%20soft%20film%20grain%2C%20elegant%20minimal%20vertical%20composition%2C%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-01&orientation=portrait",
  },
  {
    id: "e2",
    code: "A-02",
    title: "周年回顾合集",
    type: "合集",
    period: "历年回顾",
    duration: "长篇合辑",
    format: "套装发行",
    tags: ["合集", "精选"],
    desc: "按时间线把历年较有代表性的内容重新编排成套装，一部即可看到不同阶段的风格变化。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20vintage%20film%20strips%20curling%20in%20warm%20amber%20light%20on%20dark%20background%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20nostalgic%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-02&orientation=portrait",
  },
  {
    id: "e3",
    code: "A-03",
    title: "剧情长篇主线",
    type: "剧情",
    period: "持续更新",
    duration: "完整长片",
    format: "单部发行",
    tags: ["剧情"],
    desc: "以完整故事线为核心的主体内容，单部即能独立观看，是作品体系里更新最稳定的一类。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20a%20dim%20rainy%20window%20with%20warm%20amber%20street%20lights%20blurred%20behind%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20melancholic%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-03&orientation=portrait",
  },
  {
    id: "e4",
    code: "A-04",
    title: "主题企划单元",
    type: "企划",
    period: "每期更新",
    duration: "中篇内容",
    format: "连载发行",
    tags: ["企划"],
    desc: "每期设定不同主题，内容随主题变化，适合按设定兴趣挑着看，不必按顺序追。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20layered%20translucent%20amber%20glass%20panels%20with%20soft%20light%20passing%20through%2C%20deep%20brown%20background%2C%20film%20grain%20texture%2C%20elegant%20minimal%20poster%20aesthetic%2C%20calm%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-04&orientation=portrait",
  },
  {
    id: "e5",
    code: "A-05",
    title: "精选短篇合辑",
    type: "精选",
    period: "持续更新",
    duration: "短篇合集",
    format: "合辑发行",
    tags: ["精选", "合集"],
    desc: "把多段篇幅较短的内容收录在同一套合辑里，节奏紧凑，适合一次看多个片段。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20scattered%20light%20bokeh%20and%20golden%20dust%20over%20a%20dark%20textured%20wall%2C%20warm%20amber%20tones%2C%20deep%20brown%20shadows%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20dreamy%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-05&orientation=portrait",
  },
  {
    id: "e6",
    code: "A-06",
    title: "跨界合作专题",
    type: "企划",
    period: "不定期",
    duration: "中长篇",
    format: "专题发行",
    tags: ["企划", "纪念"],
    desc: "与外部团体或品牌共同策划的联动内容，风格与常规作品有区别，通常带有明显合作标识。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20a%20vintage%20camera%20silhouette%20against%20warm%20amber%20haze%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20retro%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-06&orientation=portrait",
  },
  {
    id: "e7",
    code: "A-07",
    title: "幕后花絮与特典",
    type: "特典",
    period: "随片发行",
    duration: "短片附赠",
    format: "附赠内容",
    tags: ["特典"],
    desc: "正片之外的幕后记录与特典影像，多为随片附赠，长度较短，作为补充视角观看。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20a%20theatre%20aisle%20with%20warm%20amber%20guide%20lights%20fading%20into%20darkness%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20mysterious%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-07&orientation=portrait",
  },
  {
    id: "e8",
    code: "A-08",
    title: "高清修复合集",
    type: "合集",
    period: "早期作品",
    duration: "长篇合辑",
    format: "重制发行",
    tags: ["合集", "精选"],
    desc: "对较早发布的内容进行画质修复后重新整理发行，画面更清晰，内容方向保持不变。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20restored%20film%20frames%20glowing%20warm%20amber%20on%20a%20dark%20table%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20nostalgic%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-08&orientation=portrait",
  },
  {
    id: "e9",
    code: "A-09",
    title: "数字线上限定",
    type: "数字",
    period: "近年内容",
    duration: "短篇为主",
    format: "线上发行",
    tags: ["数字"],
    desc: "只在线上渠道发行的内容，更新节奏较快，篇幅通常比常规长篇更精简。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20a%20glowing%20digital%20screen%20edge%20with%20warm%20amber%20reflection%20on%20dark%20surface%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20modern%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-09&orientation=portrait",
  },
  {
    id: "e10",
    code: "A-10",
    title: "海外版发行合集",
    type: "合集",
    period: "近年内容",
    duration: "长篇合辑",
    format: "海外发行",
    tags: ["合集", "海外"],
    desc: "面向海外市场重新剪辑与包装的版本，通常带有字幕或本地化说明，方便不同地区浏览。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20composition%20of%20a%20world%20map%20outline%20made%20of%20warm%20amber%20light%20over%20dark%20background%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20poster%20aesthetic%2C%20cinematic%20moody%20atmosphere%2C%20no%20people%2C%20no%20text&width=600&height=800&seq=yua-cover-10&orientation=portrait",
  },
];

export const collections: CollectionInfo[] = [
  {
    id: "col1",
    name: "年份合集",
    desc: "以时间为主线，把同一阶段的条目集中收录，方便观察不同时期的风格与选题变化。",
    points: [
      "按发布年份或阶段划分，纵向对比清晰",
      "适合想成体系回顾、而不是单部挑选的人",
      "条目之间通常有编号，便于查找与记录",
    ],
    image:
      "https://readdy.ai/api/search-image?query=abstract%20stacked%20vintage%20film%20boxes%20and%20photo%20albums%20arranged%20neatly%20on%20dark%20wood%2C%20warm%20amber%20light%2C%20deep%20brown%20shadows%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20nostalgic%20warm%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-col-01&orientation=landscape",
  },
  {
    id: "col2",
    name: "主题合集",
    desc: "围绕某一设定、场景或情绪把条目重新聚合，主题明确，挑选时更凭兴趣。",
    points: [
      "按主题而非时间组织，跨年份组合",
      "每个主题内条目风格相对统一",
      "适合已经明确偏好方向的人浏览",
    ],
    image:
      "https://readdy.ai/api/search-image?query=abstract%20calendar%20pages%20and%20time%20ribbon%20glowing%20warm%20amber%20on%20a%20dark%20surface%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20organized%20moody%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-col-02&orientation=landscape",
  },
  {
    id: "col3",
    name: "精选榜单合集",
    desc: "依据口碑与讨论度挑出高关注条目，按名次或推荐程度排列，减少挑选成本。",
    points: [
      "带有推荐理由与排序，参考性强",
      "条目数量有限，浏览时间可控",
      "适合作为第一次接触时的入口",
    ],
    image:
      "https://readdy.ai/api/search-image?query=abstract%20golden%20trophy%20and%20ranking%20podium%20abstract%20shapes%20in%20warm%20amber%20light%20on%20dark%20background%2C%20deep%20brown%20tones%2C%20soft%20film%20grain%2C%20elegant%20minimal%20editorial%20composition%2C%20celebratory%20moody%20atmosphere%2C%20no%20people&width=800&height=600&seq=yua-col-03&orientation=landscape",
  },
];

export const seriesList: SeriesInfo[] = [
  {
    id: "se1",
    name: "季度连载企划",
    desc: "每一季围绕一个核心概念推出若干条目，季与季之间主题相对独立，但整体风格保持连贯。",
    freq: "按季度节奏推出",
  },
  {
    id: "se2",
    name: "编号主题系列",
    desc: "以固定编号命名，条目顺序即观看顺序，前一条目往往为后一条目做铺垫。",
    freq: "按编号连续推出",
  },
  {
    id: "se3",
    name: "场景设定系列",
    desc: "固定一种场景或设定，每期更换细节与呈现方式，方便直接对比同一设定下的不同处理。",
    freq: "不定期连续更新",
  },
  {
    id: "se4",
    name: "合作联动系列",
    desc: "与固定合作方长期共同制作，带有统一的系列标识，风格辨识度较高。",
    freq: "按合作周期推出",
  },
  {
    id: "se5",
    name: "回顾重制系列",
    desc: "把过去的经典条目按系列重新整理发行，加入修复或补充内容，便于系统补看。",
    freq: "阶段性集中推出",
  },
];

export const recommendList: RecommendItem[] = [
  {
    id: "r1",
    title: "第一次接触：从精选榜单看起",
    audience: "完全没接触过，想先了解整体水平",
    reason: "精选榜单条目数量有限且带有推荐理由，能在较短时间内建立对风格与水准的整体印象。",
    tags: ["入门", "精选"],
  },
  {
    id: "r2",
    title: "喜欢故事感：优先剧情长片",
    audience: "在意叙事完整、代入感的浏览者",
    reason: "剧情长片以完整故事线为核心，单部即可独立观看，不需要依赖前后条目也能看懂。",
    tags: ["剧情", "长片"],
  },
  {
    id: "r3",
    title: "时间有限：用短篇合辑快速浏览",
    audience: "只想碎片时间随手看看的人",
    reason: "短篇合辑把多段内容打包在一起，每段节奏紧凑，适合不想投入整段时间的场景。",
    tags: ["合辑", "轻量"],
  },
  {
    id: "r4",
    title: "想成体系：按年份合集顺序看",
    audience: "喜欢系统整理、做记录的浏览者",
    reason: "年份合集按时间线组织，条目之间有编号可循，便于横向对比不同阶段的差异。",
    tags: ["合集", "系统"],
  },
  {
    id: "r5",
    title: "追新内容：留意线上限定与近期企划",
    audience: "关注更新节奏、想看新内容的人",
    reason: "线上限定与近期企划推出频率较高，篇幅精简，适合保持更新关注度的浏览习惯。",
    tags: ["数字", "更新"],
  },
  {
    id: "r6",
    title: "看重画质：优先修复合集",
    audience: "对画面清晰度有要求的浏览者",
    reason: "修复合集对早期条目重新处理画质，保留原有内容方向的同时提升了观看舒适度。",
    tags: ["合集", "画质"],
  },
];

export const faqList: FaqItem[] = [
  {
    id: "f1",
    q: "这个页面主要提供什么内容？",
    a: "本页把与「三上悠亚电影」相关的浏览方向整理在一起，按作品、合集、列表、大全、系列与推荐六个方向分区呈现，并提供一份可在本页筛选检索的条目清单，方便快速定位想看的方向。",
  },
  {
    id: "f2",
    q: "作品、合集、列表、大全之间是什么关系？",
    a: "它们描述的是同一种内容的四种查看角度：作品偏整体范围，合集强调重新编排的套装，列表是把条目按顺序排开，大全则强调覆盖面。可以先从需求出发选择角度，再回到条目本身。",
  },
  {
    id: "f3",
    q: "页面上的筛选和搜索能做什么？",
    a: "筛选与搜索都只作用于本页已收录的条目，通过标签与关键词即时缩小范围，不涉及跳转外部页面，也不会上传或保存任何输入内容。",
  },
  {
    id: "f4",
    q: "条目编号（如 A-01）代表什么？",
    a: "编号仅用于在本页内区分与定位条目，方便你在筛选、对比或记录时快速指代某一条，并不代表官方发布顺序或正式编号。",
  },
  {
    id: "f5",
    q: "为什么条目里没有具体的作品名称？",
    a: "本页以介绍作品方向、类型与整理方式为主，因此条目描述的是内容形态与浏览思路，而非逐部罗列名称。这样即便资料更新，页面提供的判断思路依然有效。",
  },
  {
    id: "f6",
    q: "推荐部分是怎么给出的？",
    a: "推荐按浏览场景与关注重点划分，例如入门、剧情向、时间有限等，每条都附有推荐理由与适用人群，方便你按自己的情况对号入座，而不是给出一份绝对排名。",
  },
];